import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { CATEGORY_LABELS, fmtDate, LevelBadge, daysLabel, brandLogo } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, QrCode, Repeat, Bell, FileText, Boxes, Trash2, ShieldCheck, Search } from "lucide-react";

const TRANSFER_FIELDS = [
  ["name", "Nom du produit"], ["brand", "Marque"], ["model", "Modèle"],
  ["serial", "N° de série"], ["purchase_date", "Date d'achat"], ["warranty_months", "Garantie"],
];

export default function PassportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tOpen, setTOpen] = useState(false);
  const [tEmail, setTEmail] = useState("");
  const [tFields, setTFields] = useState(["name", "brand", "model", "serial", "purchase_date", "warranty_months"]);
  const [tPackage, setTPackage] = useState(null);

  const load = () => api.get(`/items/${id}`).then((r) => setData(r.data)).catch(() => navigate("/app/memoire"));
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="text-muted-foreground">Chargement…</div>;
  const it = data.item;

  const toggleField = (f) => setTFields((p) => p.includes(f) ? p.filter((x) => x !== f) : [...p, f]);

  const doTransfer = async () => {
    const { data: pkg } = await api.post(`/items/${id}/transfer`, { to_email: tEmail, fields: tFields, include_history: true });
    setTPackage(pkg);
    toast.success("Transfer package créé");
  };

  const del = async () => { await api.delete(`/items/${id}`); toast.success("Bien supprimé"); navigate("/app/memoire"); };

  const manualQuery = [it.brand, it.model || it.name, "notice mode d'emploi pdf"].filter(Boolean).join(" ");
  const manualUrl = `https://www.google.com/search?q=${encodeURIComponent(manualQuery)}`;

  const infoRows = [
    ["Marque", it.brand], ["Modèle", it.model], ["N° de série", it.serial],
    ["Date d'achat", it.purchase_date && fmtDate(it.purchase_date)],
    ["Prix", it.price != null && `${it.price} €`], ["Lieu d'achat", it.retailer],
    ["Réparateur / SAV", it.repairer],
    ["Garantie", it.warranty_months && `${it.warranty_months} mois`],
    ["Emplacement", it.location], ["Kilométrage", it.mileage && `${it.mileage.toLocaleString("fr-FR")} km`],
  ].filter(([, v]) => v);

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} data-testid="passport-back-btn" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Retour
      </button>

      <div className="omy-glass rounded-3xl p-7">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-primary">Passeport OMY · {CATEGORY_LABELS[it.category] || it.category}</span>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-1">{it.name}</h1>
            {it.notes && <p className="text-sm text-muted-foreground mt-2 max-w-lg">{it.notes}</p>}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {it.brand && (
              <img src={brandLogo(it.brand)} alt={it.brand} data-testid="passport-brand-logo"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="h-9 w-auto max-w-[110px] object-contain rounded bg-white/90 px-1.5 py-1" />
            )}
            {it.image_url ? (
              <img src={`${API}${it.image_url}`} alt={it.name} data-testid="passport-image"
                className="w-20 h-20 rounded-2xl object-cover border border-white/10" />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center" data-testid="passport-qr">
                <QrCode size={38} className="text-foreground/70" />
              </div>
            )}
          </div>
        </div>

        {it.official && (
          <div data-testid="official-badge" className="mt-5 flex items-start gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
            <ShieldCheck size={20} className="text-emerald-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-emerald-300">Garantie officielle {it.official.company}{it.brand ? ` · ${it.brand}` : ""}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {it.official.warranty_months ? `Garantie constructeur ${it.official.warranty_months} mois. ` : ""}
                {it.official.manual_note || "Informations officielles vérifiées, poussées par le fabricant."}
              </p>
              {it.official.documentation_url && (
                <a href={it.official.documentation_url} target="_blank" rel="noreferrer" data-testid="official-doc-link"
                  className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:underline mt-1"><FileText size={12} /> Notice officielle</a>
              )}
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-6">
          {infoRows.map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-white/5 py-2 text-sm">
              <span className="text-muted-foreground">{k}</span>
              <span className="font-medium">{v}</span>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mt-6">
          <Dialog open={tOpen} onOpenChange={setTOpen}>
            <DialogTrigger asChild>
              <Button data-testid="transfer-btn" className="bg-primary hover:bg-primary/90 rounded-full">
                <Repeat size={16} className="mr-1.5" /> Transférer avec OMY
              </Button>
            </DialogTrigger>
            <DialogContent className="omy-glass">
              <DialogHeader><DialogTitle>Transférer la propriété</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">Sélectionne les informations transmises. Tes données privées ne sont jamais transférées automatiquement.</p>
              <div className="space-y-2 mt-2">
                {TRANSFER_FIELDS.map(([f, label]) => (
                  <label key={f} className="flex items-center gap-3 text-sm cursor-pointer">
                    <Checkbox data-testid={`transfer-field-${f}`} checked={tFields.includes(f)} onCheckedChange={() => toggleField(f)} />
                    {label}
                  </label>
                ))}
              </div>
              <div className="mt-2">
                <Label>Email de l'acquéreur</Label>
                <Input data-testid="transfer-email-input" value={tEmail} onChange={(e) => setTEmail(e.target.value)} className="mt-1 bg-black/30" placeholder="acheteur@email.com" />
              </div>
              {tPackage && (
                <div data-testid="transfer-package" className="rounded-xl bg-black/30 p-3 text-xs">
                  <p className="text-primary font-medium mb-1">Transfer package · code {tPackage.code}</p>
                  <p className="text-muted-foreground">Invitation prête pour {tPackage.to_email || "l'acquéreur"} · {Object.keys(tPackage.package).length} champs · historique inclus</p>
                </div>
              )}
              <DialogFooter>
                <Button data-testid="transfer-confirm-btn" onClick={doTransfer} className="bg-primary hover:bg-primary/90 rounded-full">Créer le package</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button data-testid="delete-item-btn" variant="ghost" onClick={del} className="text-muted-foreground hover:text-destructive">
            <Trash2 size={16} />
          </Button>
        </div>

        <a href={manualUrl} target="_blank" rel="noreferrer" data-testid="find-manual-btn"
          className="mt-4 inline-flex items-center gap-2 text-sm text-primary hover:underline">
          <Search size={15} /> Trouver la notice en ligne
        </a>
      </div>

      {/* Timeline */}
      <div className="omy-glass rounded-3xl p-7">
        <h2 className="font-display text-lg font-medium mb-5">Timeline — la mémoire de l'objet</h2>
        <div className="relative pl-6">
          <div className="absolute left-[7px] top-1 bottom-1 w-px bg-white/10" />
          {data.timeline.map((ev, i) => (
            <div key={ev.id} data-testid={`timeline-event-${i}`} className="relative pb-5 last:pb-0">
              <div className="absolute -left-[22px] top-1 w-3.5 h-3.5 rounded-full bg-primary ring-4 ring-background" />
              <p className="text-sm font-medium">{ev.label}</p>
              <p className="text-xs text-muted-foreground">{fmtDate(ev.date)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Reminders */}
      {data.reminders.length > 0 && (
        <div className="omy-glass rounded-3xl p-7">
          <h2 className="font-display text-lg font-medium mb-4 flex items-center gap-2"><Bell size={18} className="text-accent" /> Échéances</h2>
          <div className="space-y-3">
            {data.reminders.map((r, i) => (
              <div key={r.id} data-testid={`passport-reminder-${i}`} className="flex items-center justify-between text-sm">
                <span>{r.title}</span>
                <div className="flex items-center gap-3">
                  <span className="text-muted-foreground">{r.mileage_due ? `${r.mileage_due.toLocaleString("fr-FR")} km` : daysLabel(r.days_left)}</span>
                  <LevelBadge level={r.level} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents */}
      {data.documents.length > 0 && (
        <div className="omy-glass rounded-3xl p-7">
          <h2 className="font-display text-lg font-medium mb-4 flex items-center gap-2"><FileText size={18} className="text-primary" /> Documents liés</h2>
          <div className="space-y-2">
            {data.documents.map((d) => (
              <div key={d.id} className="flex items-center gap-2 text-sm"><Boxes size={14} className="text-muted-foreground" />{d.original_filename}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
